import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChartBar, MagnifyingGlass, Sparkle, LockKey, Info, Lightbulb } from "@phosphor-icons/react";
import { PageShell } from "../components/Layout";
import SEO from "../components/SEO";
import PaywallGate from "../components/PaywallGate";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { startGoogleSignIn } from "../lib/capacitorAuth";
import { PatternPanel, LockedPanelPreview } from "../components/patterns/PatternPanel";

const TOOL = "celebrity_pattern_recognizer";

// NDJSON stream → progress frames + final result. Returns null if the proxy buffered (caller falls back).
async function streamAnalyze(body, onProgress) {
  const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/celebrities/pattern-recognizer/analyze/stream`, {
    method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let msg = resp.status === 429 ? "Too many requests. Please wait a minute." : `Error ${resp.status}`;
    try { msg = (await resp.json())?.detail || msg; } catch { /* not JSON */ }
    throw new Error(typeof msg === "string" ? msg : "Could not analyse that question.");
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let result = null;
  let sawFrame = false;
  const watchdog = setTimeout(() => { if (!sawFrame) reader.cancel().catch(() => {}); }, 20000);
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      sawFrame = true;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const evt = JSON.parse(line);
          if (evt.type === "progress") onProgress({ done: evt.done, total: evt.total, current: evt.current });
          else if (evt.type === "result") result = evt.data;
        } catch { /* skip malformed frame */ }
      }
    }
  } finally {
    clearTimeout(watchdog);
  }
  return result;
}

function ProgressMeter({ p }) {
  const pct = p.total ? Math.round((p.done / p.total) * 100) : 0;
  return (
    <div className="rounded-2xl bg-white border border-amber-200/70 p-5" data-testid="pattern-progress">
      <div className="flex items-center justify-between text-sm text-[#3F2D1B]">
        <span data-testid="pattern-progress-label">
          {p.total ? `Scanning ${p.done} of ${p.total} charts` : "Loading the category…"}
          {p.current && <span className="text-[#3F2D1B]/60"> · {p.current}</span>}
        </span>
        <span className="font-serif-display text-2xl tabular-nums text-amber-600" data-testid="pattern-progress-pct">{pct}%</span>
      </div>
      <div className="mt-3 h-2.5 rounded-full overflow-hidden" style={{ background: "rgba(255,184,0,0.18)" }}>
        <div className="h-full rounded-full transition-[width] duration-200 ease-out" data-testid="pattern-progress-bar" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#FFB800,#FB8500,#10B981)", boxShadow: "0 0 12px rgba(251,133,0,0.6)" }} />
      </div>
    </div>
  );
}

function SignInWall() {
  return (
    <div className="max-w-md mx-auto text-center rounded-2xl bg-white border border-amber-200 p-8 mt-10" data-testid="pattern-signin-wall">
      <LockKey size={28} className="mx-auto text-amber-600" weight="duotone" />
      <h2 className="font-serif-display text-2xl mt-3 text-[#3F2D1B]">Sign in to use the Pattern Recognizer</h2>
      <p className="text-sm text-[#3F2D1B]/70 mt-2">The celebrity database is for members only.</p>
      <button className="btn-primary rounded-full px-6 py-2.5 mt-5 text-sm" onClick={() => startGoogleSignIn(window.location.href)} data-testid="pattern-signin-btn">Sign in</button>
    </div>
  );
}

function ChipGroup({ title, items, onPick, showAll, prefix, testid, highlight = false, mobileHidden = false }) {
  if (!items.length) return null;
  return (
    <div className={`mt-4 ${mobileHidden && !showAll ? "hidden sm:block" : ""}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold mb-2 ${highlight ? "text-emerald-600" : "text-[#3F2D1B]/55"}`}>{title}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((q, i) => (
          <button type="button" key={q} onClick={() => onPick(q)} className={`${!showAll && highlight && i >= 4 ? "hidden sm:inline-flex" : "inline-flex"} text-[11px] px-3 py-1.5 rounded-full border transition-colors ${highlight ? "border-emerald-400/60 bg-emerald-50 hover:bg-emerald-100 text-emerald-900" : "border-amber-300/60 bg-amber-50 hover:bg-amber-100 text-[#3F2D1B]"}`} data-testid={`${prefix}-${i}`}>{q}</button>
        ))}
      </div>
    </div>
  );
}

const RATING_HINT = { AA: "From birth certificate / record", A: "From memory / news", B: "Biography / autobiography", C: "Caution: no source", X: "No birth time / unverified" };

function RatingPicker({ meta, cat, ratings, setRatings }) {
  const toggle = (r) => setRatings((cur) => (cur.includes(r) ? cur.filter((x) => x !== r) : [...cur, r]));
  const n = meta.rodden_ratings.filter((r) => ratings.includes(r)).reduce((s, r) => s + (cat?.ratings?.[r] || 0), 0);
  return (
    <div className="mt-4" data-testid="pattern-rating-picker">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-xs font-semibold text-[#3F2D1B]/70">Rodden rating (birth-data accuracy)</span>
        <span className="text-[11px] text-[#3F2D1B]/60" data-testid="pattern-rating-count">{n} of {cat?.count || 0} celebrities selected</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        {meta.rodden_ratings.map((r) => {
          const on = ratings.includes(r);
          return (
            <button type="button" key={r} onClick={() => toggle(r)} title={RATING_HINT[r]} aria-pressed={on}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-amber-500 text-white border-amber-500" : "bg-transparent text-[#3F2D1B]/60 border-amber-300/60 hover:border-amber-500"}`}
              data-testid={`pattern-rating-${r}`}>
              {r} <span className={on ? "text-white/80" : "text-[#3F2D1B]/45"}>({cat?.ratings?.[r] || 0})</span>
            </button>
          );
        })}
        <button type="button" onClick={() => setRatings(["AA", "A"])} className="px-3 py-1.5 rounded-full text-[11px] underline text-amber-700" data-testid="pattern-rating-preset-accurate">Accurate only (AA + A)</button>
        <button type="button" onClick={() => setRatings(meta.rodden_ratings)} className="px-3 py-1.5 rounded-full text-[11px] underline text-amber-700" data-testid="pattern-rating-preset-all">All</button>
      </div>
    </div>
  );
}

function QueryForm({ meta, category, setCategory, question, setQuestion, onRun, busy, ratings, setRatings }) {
  const [showAll, setShowAll] = useState(false);
  const cat = meta.categories.find((c) => c.slug === category);
  const catQs = cat?.questions || [];
  const generalQs = meta.examples.filter((q) => !catQs.includes(q));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onRun(); }} className="rounded-2xl bg-white border border-amber-200/70 p-5 sm:p-7 shadow-sm" data-testid="pattern-query-form">
      <div className="grid sm:grid-cols-[220px_1fr] gap-4">
        <label className="text-xs font-semibold text-[#3F2D1B]/70">Category
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="mt-1 w-full rounded-xl border border-amber-200 bg-[#FFFBF3] px-3 py-2.5 text-sm" data-testid="pattern-category-select">
            {meta.categories.map((c) => <option key={c.slug} value={c.slug} disabled={!c.count}>{c.label} ({c.count}{c.count ? "" : " · coming soon"})</option>)}
          </select>
        </label>
        <label className="text-xs font-semibold text-[#3F2D1B]/70">Your question
          <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} placeholder="e.g. What was the position of Rahu in the Varshaphal when they won?" className="mt-1 w-full rounded-xl border border-amber-200 bg-[#FFFBF3] px-3 py-2.5 text-sm" data-testid="pattern-question-input" />
        </label>
      </div>
      <RatingPicker meta={meta} cat={cat} ratings={ratings} setRatings={setRatings} />
      <ChipGroup title={`Suggested for ${cat?.label || "this category"}`} items={catQs} onPick={setQuestion} showAll={showAll} prefix="pattern-cat-q" highlight testid="pattern-category-questions" />
      {!showAll && <button type="button" onClick={() => setShowAll(true)} className="block sm:hidden text-[11px] mt-2 underline text-amber-700" data-testid="pattern-examples-more">Show all questions</button>}
      <ChipGroup title="General questions" items={generalQs} onPick={setQuestion} showAll={showAll} prefix="pattern-example" testid="pattern-examples" mobileHidden />
      <button type="submit" disabled={busy || !question.trim() || !ratings.length} className="btn-primary rounded-full px-7 py-3 mt-5 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="pattern-analyze-btn">
        <MagnifyingGlass size={16} weight="bold" /> {busy ? "Scanning every chart…" : "Recognise the pattern"}
      </button>
    </form>
  );
}

function ResultHeader({ r }) {
  return (
    <div className="rounded-2xl bg-[#3F2D1B] text-[#FAF4E8] p-5 sm:p-6" data-testid="pattern-result-header">
      <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300">We understood your question as</div>
      <div className="font-serif-display text-xl sm:text-2xl mt-1" data-testid="pattern-intent-label">{r.intent.label}</div>
      <div className="flex flex-wrap gap-3 mt-4 text-xs">
        <span className="px-3 py-1 rounded-full bg-white/10" data-testid="pattern-sample-size">{r.sample_size} charts analysed</span>
        <span className="px-3 py-1 rounded-full bg-white/10">{r.category.label} · {r.total_in_category} matching</span>
        <span className="px-3 py-1 rounded-full bg-white/10" data-testid="pattern-result-ratings">Rodden: {r.ratings?.join(", ")}</span>
        {r.excluded > 0 && <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-200" data-testid="pattern-excluded">{r.excluded} skipped ({r.excluded_reason})</span>}
      </div>
    </div>
  );
}

function Insights({ r }) {
  if (!r.insights?.length) return null;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 p-5 sm:p-6" data-testid="pattern-insights">
      <h3 className="font-serif-display text-xl text-[#3F2D1B] flex items-center gap-2"><Lightbulb size={20} weight="duotone" className="text-amber-600" /> Insights</h3>
      <ul className="mt-3 space-y-2">
        {r.insights.map((t, i) => <li key={i} className="text-sm text-[#3F2D1B]/85 leading-relaxed pl-4 border-l-2 border-amber-400" data-testid={`pattern-insight-${i}`}>{t}</li>)}
      </ul>
    </div>
  );
}

function LockedResult({ r, meta, onUnlocked }) {
  return (
    <div className="relative" data-testid="pattern-locked-preview">
      <div className="grid md:grid-cols-2 gap-5 blur-[6px] select-none pointer-events-none" aria-hidden="true">
        {r.panels.slice(0, 4).map((p) => <LockedPanelPreview key={p.title} title={p.title} />)}
      </div>
      <PaywallGate tool={TOOL} toolLabel="Celebrity Pattern Recognizer" birthDetails={meta.birth_details} variant="standalone" forWhat="for 30 days" footnote="One-time payment · 30 days of unlimited questions across every category · included with VIP" pitch={`Unlock the full statistics for all ${r.sample_size} charts, plus every other category and question, for ${meta.access_days} days.`} onUnlocked={onUnlocked} className="mt-2" />
    </div>
  );
}

export default function CelebrityPatternRecognizerPage() {
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const [meta, setMeta] = useState(null);
  const [category, setCategory] = useState("lottery-winners");
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const loadMeta = () => api.get("/celebrities/pattern-recognizer/meta").then(({ data }) => {
    setMeta(data);
    setCategory((c) => (data.categories.some((x) => x.slug === c) ? c : data.categories.find((x) => x.count)?.slug || ""));
  }).catch(() => toast.error("Could not load the recognizer."));

  useEffect(() => { if (user) loadMeta(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const [progress, setProgress] = useState(null);
  const [ratings, setRatings] = useState(["AA", "A", "B", "C", "X"]);

  const run = async () => {
    setBusy(true);
    setResult(null);
    setProgress({ done: 0, total: 0, current: "" });
    const body = { category, question, ratings };
    try {
      const data = await streamAnalyze(body, setProgress);
      setResult(data || (await api.post("/celebrities/pattern-recognizer/analyze", body)).data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not analyse that question.");
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  return (
    <PageShell>
      <SEO title="Celebrity Placements Pattern Recognizer · AstroKuberChat" description="Ask a plain-English question about celebrity charts. We scan every chart in the category and chart the statistical pattern." />
      <section className="max-w-6xl mx-auto px-5 lg:px-10 pt-10 pb-24" data-testid="pattern-recognizer-page">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700"><ChartBar size={14} weight="fill" /> Premium research tool · {formatPrice(meta?.price ?? 3000)} / 30 days</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-light mt-3 text-[#3F2D1B] leading-tight">Celebrity Placements <span className="italic text-gold">Pattern Recognizer</span></h1>
        <p className="text-sm sm:text-base text-[#3F2D1B]/70 mt-4 max-w-3xl">Pick a category, ask a question, and we calculate the answer for every celebrity chart in it, then chart how often each sign, house or dasha lord shows up compared with chance. Pure Swiss Ephemeris maths, no AI guesswork.</p>
        {meta?.unlocked && meta.access_expires_at && (
          <div className="mt-4 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800" data-testid="pattern-access-badge">
            <Sparkle size={12} weight="fill" /> Pass active until {new Date(meta.access_expires_at).toLocaleDateString()}
          </div>
        )}
        {!user ? <SignInWall /> : !meta ? <div className="h-40 mt-8 rounded-2xl bg-amber-100/50 animate-pulse" /> : (
          <div className="space-y-6 mt-8">
            <QueryForm meta={meta} category={category} setCategory={setCategory} question={question} setQuestion={setQuestion} onRun={run} busy={busy} ratings={ratings} setRatings={setRatings} />
            {progress && <ProgressMeter p={progress} />}
            {result && <ResultHeader r={result} />}
            {result && !result.unlocked && <LockedResult r={result} meta={meta} onUnlocked={() => { loadMeta(); run(); }} />}
            {result?.unlocked && <Insights r={result} />}
            {result?.unlocked && result.vargottama?.map((v) => (
              <div key={v.planet} className="rounded-xl bg-violet-50 border border-violet-200 px-4 py-3 text-sm text-violet-900" data-testid={`pattern-vargottama-${v.planet}`}>
                <strong>{v.planet} vargottama (same sign in D1 &amp; D9):</strong> {v.same_sign}/{v.n} charts · {v.pct}% vs {v.expected_pct}% by chance
              </div>
            ))}
            {result?.unlocked && <div className="grid gap-6">{result.panels.map((p, i) => <PatternPanel key={p.title} panel={p} index={i} />)}</div>}
            <p className="text-[11px] text-[#3F2D1B]/55 flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" /> Varshaphal and dasha questions use each celebrity's main recorded event (the win date for lottery winners, the rise date for everyone else). Patterns from small samples are hints, not proof. <Link to="/celebrities" className="underline">Browse the database</Link></p>
          </div>
        )}
      </section>
    </PageShell>
  );
}
